<template>
    <section class="doc-section">

        <!-- Selector de Plataforma -->
        <div class="mb-6">
            <BotonesNavegacion @cambio-plataforma="plataformaActual = $event" />
        </div>

        <header class="header-container">
            <h1>Proyección de Movimiento</h1>
            <p class="description">
                El módulo de histórico permite reproducir trayectos anteriores, consultar posiciones 
                específicas y analizar eventos registrados de cualquier dispositivo en el mapa.
            </p>
        </header>

        <div class="two-col">
            <div class="col-image">
                <div class="carousel-box">
                    <transition name="fade" mode="out-in">
                        <CarruselAciqui
                            :key="plataformaActual"
                            :items="imagenesModuloPrincipalMostrar"
                        />
                    </transition>                </div>
            </div>

            <div class="col-steps">
                <p class="steps-label">Configuración de Consulta</p>
                <div class="info-grid">
                    <div
                        v-for="(info, index) in plataformaActual === 'web'
                            ? referenciasPrincipales
                            : referenciasPrincipalesMovil"
                        :key="index"
                        class="step-card"
                    >                        <div class="step-badge">{{ info.letra }}</div>
                        <div>
                            <div class="step-title">{{ info.titulo }}</div>
                            <div class="step-desc">{{ info.descripcion }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="section-divider">
            <h2>Herramientas del Mapa</h2>
        </div>

        <p class="main-desc">
            Utilice los controles integrados para gestionar la reproducción y el análisis métrico del recorrido:
        </p>

        <div class="carousel-box">
            <transition name="fade" mode="out-in">
                <CarruselAciqui
                    :key="plataformaActual"
                    :items="imagenesHerramientasMostrar"
                />
            </transition>        
        </div>

        <div class="section-divider">
            <h2>Descarga de Historial</h2>
        </div>

        <div class="form-grid">
            <div class="field-row">
                <div class="field-icon-box">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 16l-4-4h2.5V4h3v8H16l-4 4z" fill="currentColor"/>
                        <path d="M4 20h16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </div>
                <div>
                    <div class="field-label">
                        {{ plataformaActual === 'web'
                            ? 'F) Descargar en Excel'
                            : 'E) Descargar en Excel'
                        }}
                    </div>                   
                    <div class="field-desc">Exporta el historial completo con tiempos de estadía y alertas en formato XLSX.</div>
                </div>
            </div>
            <div class="field-row">
                <div class="field-icon-box">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 16l-4-4h2.5V4h3v8H16l-4 4z" fill="currentColor"/>
                        <path d="M4 20h16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </div>
                <div>
                    <div class="field-label">
                        {{ plataformaActual === 'web'
                            ? 'G) Descargar en CSV'
                            : 'F) Descargar en CSV'
                        }}
                    </div>
                    <div class="field-desc">Ideal para procesar grandes volúmenes de datos de posiciones en texto plano.</div>
                </div>
            </div>
        </div>

        <div class="doc-note note-info">
            <svg class="note-icon" width="15" height="15" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                <path d="M8 5v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                <circle cx="8" cy="11.5" r="0.75" fill="currentColor"/>
            </svg>
            <div class="note-body">
                <span class="note-label">Simbología del Mapa</span>
                <p>El inicio de ruta se marca en <strong>verde</strong>, el final en <strong>rojo</strong> y el trayecto se proyecta mediante una <strong>línea azul</strong> continua.</p>
            </div>
        </div>

    </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import CarruselAciqui from '../Carruselimg.vue';
import BotonesNavegacion from '../BotonesNavegacion.vue';

const plataformaActual = ref('web');

/* ── Datos Web ───────────────────────────────────── */
const webData = {
    moduloPrincipal: [
        {
            nombre: "Módulo Histórico",
            url: '/img/historicoDispositivo/Modulo_Historico.png',
            descripcion: "Interfaz principal de consulta y proyección de movimiento."
        }
    ],

    herramientas: [
        {
            nombre: "1. Puntos Estáticos",
            url: "/img/historicoDispositivo/Puntos_Estaticos.png",
            descripcion: "Visualización de puntos de estadía localizados sobre el mapa."
        },
        {
            nombre: "4. Línea de Recorrido",
            url: "/img/historicoDispositivo/Mostrar_Recorrido.png",
            descripcion: "Control para ocultar o mostrar la trayectoria azul."
        },
        {
            nombre: "5. Velocidad",
            url: "/img/historicoDispositivo/Velocidad_Recorrido.png",
            descripcion: "Ajuste de reproducción desde velocidad base hasta 5x."
        },
        {
            nombre: "8. Reproductor",
            url: "/img/historicoDispositivo/Iniciar_Recorrido.png",
            descripcion: "Botón de Play/Pause para la proyección automática."
        },
        {
            nombre: "11. Herramienta de Medición",
            url: "/img/historicoDispositivo/Medicion_Mapa.png",
            descripcion: "Regla para medir distancias métricas entre dos puntos del mapa."
        },
        {
            nombre: "15. Barra de Tiempo",
            url: "/img/historicoDispositivo/Barra_Reproduccion.png",
            descripcion: "Deslizador dinámico para navegar manualmente por el historial."
        }
    ]
};

/* ── Datos Móvil ─────────────────────────────────── */
const mobileData = {
    moduloPrincipal: [
        {
            nombre: "Módulo Histórico",
            url: '/img/historicoDispositivo/Modulo_Historico_Movil_1.png',
            descripcion: "Interfaz principal de consulta y proyección de movimiento."
        },
        {
            nombre: "Módulo Histórico",
            url: '/img/historicoDispositivo/Modulo_Historico_Movil_2.png',
            descripcion: "Interfaz principal de consulta y proyección de movimiento."
        }
    ],

    herramientas: [
        {
            nombre: "1. Puntos Estáticos",
            url: "/img/historicoDispositivo/Puntos_Estaticos_Movil.png",
            descripcion: "Visualización de puntos de estadía localizados sobre el mapa."
        },
        {
            nombre: "4. Línea de Recorrido",
            url: "/img/historicoDispositivo/Mostrar_Recorrido.png",
            descripcion: "Control para ocultar o mostrar la trayectoria azul."
        },
        {
            nombre: "5. Velocidad",
            url: "/img/historicoDispositivo/Velocidad_Recorrido.png",
            descripcion: "Ajuste de reproducción desde velocidad base hasta 5x."
        },
        {
            nombre: "8. Reproductor",
            url: "/img/historicoDispositivo/Iniciar_Recorrido.png",
            descripcion: "Botón de Play/Pause para la proyección automática."
        },
        {
            nombre: "11. Herramienta de Medición",
            url: "/img/historicoDispositivo/Medicion_Mapa_Movil.png",
            descripcion: "Regla para medir distancias métricas entre dos puntos del mapa."
        },
        {
            nombre: "15. Barra de Tiempo",
            url: "/img/historicoDispositivo/Barra_Reproduccion_Movil.png",
            descripcion: "Deslizador dinámico para navegar manualmente por el historial."
        }
    ]
};

/* ── Datos Compartidos ───────────────────────────── */
const referenciasPrincipales = [
    {
        letra: 'A',
        titulo: 'Dispositivo',
        descripcion: 'Identificador del equipo seleccionado.'
    },
    {
        letra: 'B',
        titulo: 'Rango de fechas',
        descripcion: 'Periodo inicial y final de la consulta.'
    },
    {
        letra: 'C',
        titulo: 'Botón Consultar',
        descripcion: 'Permite realizar la búsqueda del historial. Si la búsqueda cuenta con posiciones se visualizara el recorrido sobre el mapa (línea azul), representando el inicio del recorrido con un marcador verde y con un marcador rojo indicando el final del recorrido.'
    },
    {
        letra: 'D',
        titulo: 'Indicadores',
        descripcion: 'Etiqueta que muestra el número de posiciones estáticas y de alertas obtenidos de la búsqueda.'
    },
    {
        letra: 'E',
        titulo: 'Herramientas del mapa',
        descripcion: 'Utilice los controles integrados para gestionar la reproducción y el análisis métrico del recorrido.'
    }
];

/* ── Datos Compartidos ───────────────────────────── */
const referenciasPrincipalesMovil = [
    {
        letra: 'A',
        titulo: 'Dispositivo',
        descripcion: 'Identificador del equipo seleccionado.'
    },
    {
        letra: 'B',
        titulo: 'Rango de fechas',
        descripcion: 'Periodo inicial y final de la consulta.'
    },
    {
        letra: 'C',
        titulo: 'Botón Consultar',
        descripcion: 'Permite realizar la búsqueda del historial. Si la búsqueda cuenta con posiciones se visualizara el recorrido sobre el mapa (línea azul), representando el inicio del recorrido con un marcador verde y con un marcador rojo indicando el final del recorrido.'
    },
    {
        letra: 'D',
        titulo: 'Herramientas del mapa',
        descripcion: 'Utilice los controles integrados para gestionar la reproducción y el análisis métrico del recorrido.'
    }
];

/* ── Computed ────────────────────────────────────── */
const imagenesModuloPrincipalMostrar = computed(() =>
    plataformaActual.value === 'web'
        ? webData.moduloPrincipal
        : mobileData.moduloPrincipal
);

const imagenesHerramientasMostrar = computed(() =>
    plataformaActual.value === 'web'
        ? webData.herramientas
        : mobileData.herramientas
);
</script>

<style scoped>
.mb-6 {
    margin-bottom: 2rem;
}

/* ── Animación ───────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* ── Estilos Base ─────────────────────────────────── */
.doc-section { padding: 20px; }
.description, .main-desc { font-size: 15px; opacity: 0.8; margin-bottom: 24px; line-height: 1.6; }

/* ── Layout dos columnas ──────────────────────────── */
.two-col { display: flex; gap: 32px; align-items: flex-start; margin-bottom: 32px; }
.col-steps { flex: 1; }
.col-image  { flex: 1.2; position: sticky; top: 20px; }

/* ── Info Cards (Dark Mode Ready) ─────────────────── */
.steps-label {
    font-size: 11px; font-weight: 700; letter-spacing: 0.07em;
    text-transform: uppercase; color: var(--primary-color); margin-bottom: 12px;
}
.step-card {
    display: flex; gap: 14px; align-items: flex-start;
    background: var(--code-bg); border: 1px solid var(--border-color);
    border-radius: 10px; padding: 12px 16px; margin-bottom: 8px;
}
.step-badge {
    min-width: 26px; height: 26px; border-radius: 50%;
    background: var(--primary-color); color: #fff;
    font-size: 12px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.step-title { font-size: 13.5px; font-weight: 600; margin-bottom: 2px; }
.step-desc { font-size: 12.5px; opacity: 0.7; line-height: 1.4; }

/* ── UI Elements ─────────────────────────────────── */
.section-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 32px 0 16px;
}

.section-divider h2 {
    font-size: 20px;
    font-weight: 700;
    color: var(--primary-color); /* Aquí es donde toma el azul */
    margin: 0;
}
.divider-line { flex: 1; height: 1px; background: var(--border-color); }

.carousel-box { 
    border: 1px solid var(--border-color); 
    padding: 8px; border-radius: 10px; 
    background: var(--code-bg); margin-bottom: 16px; 
}

/* ── Grid de Descarga ─────────────────────────────── */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 20px 0; }
.field-row { 
    display: flex; gap: 14px; background: var(--code-bg); 
    border: 1px solid var(--border-color); border-radius: 10px; padding: 16px; 
}
.field-icon-box {
    width: 38px; height: 38px; background: var(--primary-color);
    color: #fff; border-radius: 8px; display: flex;
    align-items: center; justify-content: center; flex-shrink: 0;
}
.field-label { font-weight: 600; font-size: 14px; color: var(--primary-color); }
.field-desc { font-size: 12.5px; opacity: 0.7; margin-top: 2px; }

/* ── Notas ────────────────────────────────────────── */
.doc-note { display: flex; gap: 12px; padding: 16px; border-radius: 8px; border: 1px solid; margin-top: 16px; font-size: 14px; }
.note-info { background: var(--code-bg); border-color: var(--primary-color); color: var(--text-color); }
.note-label { color: var(--primary-color); font-weight: 700; text-transform: uppercase; font-size: 11px; display: block; }

/* ── Responsive ──────────────────────────────────── */
@media (max-width: 992px) {
    .two-col, .form-grid { flex-direction: column; grid-template-columns: 1fr; }
    .col-image { position: static; order: -1; }
}
</style>